"use client";

import React from "react";

import type { FilterPill } from "@/components/results/FilterSection";
import {
  type ResultsExperienceBrandBrowseSeed,
  type ResultsExperienceInitialState,
} from "@/components/results/ResultsExperienceInitialState";
import { sortProductsByPrice, type SortOrder } from "@/components/results/sort-utils";
import { useBrowseMenu } from "@/hooks/useBrowseMenu";
import {
  type CategoryNode,
  PRICE_RANGE_MAX_DEFAULT,
  PRICE_RANGE_MIN_DEFAULT,
  useResultsFilters,
} from "@/hooks/useResultsFilters";
import { useStickyFiltersBar } from "@/hooks/useStickyFiltersBar";
import { BRAND_SHOP_PAGE_SIZE } from "@/lib/brand-shop-pagination";
import { getNodeByDisplayPath } from "@/lib/browse-menu";
import type { Product } from "@/lib/product-utils";
import {
  appendUniqueProducts,
  deriveInitialFilters,
  getSearchParamFacets,
  shouldRetainFacetForCategoryNavigation,
  upsertFacet,
} from "@/lib/results-controller-utils";
import {
  applyPriceFilters,
  formatFacetDisplayName,
  formatFilterLabel,
  shouldRenderFacetAsAttributePill,
} from "@/lib/results-page-utils";
import { InvalidSearchCursorError, searchShopperProducts } from "@/lib/shop-agent-client";
import {
  appendUnseenProducts,
  browseRequestFromFilters,
  type ResultsSearchState,
  type ShopperFilters,
  shownFilters,
} from "@/lib/shopper-search";
import { productFromListItem } from "@/lib/shopper-search-items";

import { copyFacet, withCallerValues } from "@/lib/facet-provenance";

interface BrandBrowseResponse {
  has_more?: boolean;
  limit?: number;
  offset?: number;
  products?: Product[];
}

export interface UseBrandResultsControllerArgs {
  brandName: string;
  initialState: ResultsExperienceInitialState;
}

export interface UseBrandResultsControllerReturn {
  /** The filters the sidebar and chips show: the grid's search's. */
  searchFilters: ShopperFilters;
  searchBreadcrumbPath: string[];
  sortOrder: SortOrder;
  isSortMenuOpen: boolean;
  isSearching: boolean;
  isLoadingMore: boolean;
  loadMoreError: string | null;
  isMobileFiltersOpen: boolean;
  mobileSuggestionsExpanded: boolean;
  sortDropdownListId: string;
  filtersExpanded: boolean;
  filtersHeight: number;
  isSticky: boolean;
  filtersSectionTopRef: React.RefObject<HTMLDivElement | null>;
  filtersContentRef: React.RefObject<HTMLDivElement | null>;
  filters: ReturnType<typeof useResultsFilters>["filters"];
  activeFilters: FilterPill[];
  selectedAdvancedAttributesMap: Map<string, Set<string>>;
  categoryTree: CategoryNode[];
  currentCategoryPath?: string;
  productCount: number;
  hasProducts: boolean;
  hasMoreResults: boolean;
  sortedResults: Product[];
  isCategoryNavigationEnabled: boolean;
  isLoadingTaxonomy: boolean;
  appliedFiltersCount: number;
  setIsMobileFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setMobileSuggestionsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setFiltersExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  updateCategoryNavigationStack: (stack: Array<{ name: string; path: string }>) => void;
  toggleSortMenu: () => void;
  handleSortSelection: (nextSort: SortOrder) => void;
  restoreInitialState: () => void;
  handleCategoryNavigate: (path: string) => void;
  handleLeafCategoryToggle: (leafPath: string) => void;
  handlePriceChange: (priceRange: [number, number]) => void;
  handleColorChange: (selectedColors: string[]) => void;
  handleAdvancedAttributeChange: (attributeHandle: string, value: string) => void;
  loadMoreResults: () => Promise<void>;
}

export function useBrandResultsController({
  brandName,
  initialState,
}: UseBrandResultsControllerArgs): UseBrandResultsControllerReturn {
  // The seed's own filters (the brand), not Platform's echo of them.
  const initialSearchFilters = React.useMemo(
    () => shownFilters({ request: initialState.search.request, effectiveQuery: null }),
    [initialState.search.request]
  );
  const initialFilters = React.useMemo(
    () => deriveInitialFilters(initialSearchFilters),
    [initialSearchFilters]
  );
  const initialBreadcrumbPath = React.useMemo(
    () => initialState.searchBreadcrumbPath ?? [brandName],
    [brandName, initialState.searchBreadcrumbPath]
  );
  const initialBrandBrowseSeed = React.useMemo(
    () => initialState.brandBrowseSeed ?? null,
    [initialState.brandBrowseSeed]
  );

  const {
    index: browseMenuIndex,
    isLoading: isLoadingBrowseMenu,
    error: browseMenuError,
  } = useBrowseMenu();

  const {
    filters,
    updateCategories,
    updatePriceRange,
    updateColors,
    updateAdvanced,
    updateCategoryNavigationStack,
    replaceFilters,
    getAppliedFiltersCount,
  } = useResultsFilters(initialFilters);

  // The grid's browse; its effectiveQuery is null while an edit runs or it continues by offset.
  const [search, setSearch] = React.useState<ResultsSearchState>(initialState.search);
  const searchRef = React.useRef(search);
  searchRef.current = search;
  // The filters as the shopper last set them; each edit builds on these.
  const [searchFilters, setSearchFilters] = React.useState<ShopperFilters>(initialSearchFilters);
  const [searchResults, setSearchResults] = React.useState<Product[]>(initialState.searchResults);
  const [searchBreadcrumbPath, setSearchBreadcrumbPath] =
    React.useState<string[]>(initialBreadcrumbPath);
  const [nextCursor, setNextCursor] = React.useState<string | unknown[] | null>(
    initialState.nextCursor
  );
  const [brandBrowseSeed, setBrandBrowseSeed] =
    React.useState<ResultsExperienceBrandBrowseSeed | null>(initialBrandBrowseSeed);
  const [isSearching, setIsSearching] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [loadMoreError, setLoadMoreError] = React.useState<string | null>(null);
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("recommended");
  const [isSortMenuOpen, setIsSortMenuOpen] = React.useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = React.useState(false);
  const [mobileSuggestionsExpanded, setMobileSuggestionsExpanded] = React.useState(false);
  const sortDropdownListId = React.useId();
  const activeRequestIdRef = React.useRef(0);

  const searchParamFacets = React.useMemo(
    () => getSearchParamFacets(searchFilters),
    [searchFilters]
  );

  const syncFiltersFromSearchFilters = React.useCallback(
    (nextSearchFilters: ShopperFilters) => {
      const nextFilters = deriveInitialFilters(nextSearchFilters);
      updateColors(nextFilters.colors);
      updateAdvanced(nextFilters.advanced);
      updatePriceRange(nextFilters.priceRange);
    },
    [updateAdvanced, updateColors, updatePriceRange]
  );

  const restoreInitialState = React.useCallback(() => {
    activeRequestIdRef.current += 1;
    setIsSearching(false);
    setIsLoadingMore(false);
    setLoadMoreError(null);
    setSearch(initialState.search);
    setSearchFilters(initialSearchFilters);
    setSearchResults(initialState.searchResults);
    setSearchBreadcrumbPath(initialBreadcrumbPath);
    setNextCursor(initialState.nextCursor);
    setBrandBrowseSeed(initialBrandBrowseSeed);
    replaceFilters({
      categories: [],
      subcategories: [],
      priceRange: initialFilters.priceRange,
      colors: initialFilters.colors,
      brands: [],
      similarToBrands: [],
      advanced: initialFilters.advanced,
      categoryNavigationStack: [],
    });
  }, [
    initialBrandBrowseSeed,
    initialBreadcrumbPath,
    initialFilters.advanced,
    initialFilters.colors,
    initialFilters.priceRange,
    initialState.nextCursor,
    initialState.search,
    initialState.searchResults,
    initialSearchFilters,
    replaceFilters,
  ]);

  const executeTextSearch = React.useCallback(
    async (
      nextSearchFilters: ShopperFilters,
      nextBreadcrumbPath: string[],
      options?: { nextCategories?: string[] }
    ) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;
      // Every brand-grid search is a browse: the brand plus the shopper's filters.
      const request = browseRequestFromFilters(nextSearchFilters, {
        limit: searchRef.current.request.limit || BRAND_SHOP_PAGE_SIZE,
      });
      setIsSearching(true);
      setIsLoadingMore(false);
      setLoadMoreError(null);
      setSearchFilters(nextSearchFilters);
      setSearch({ request, effectiveQuery: null, browseMenuUuid: null, surface: "brand_grids" });
      setSearchBreadcrumbPath(nextBreadcrumbPath);
      setBrandBrowseSeed(null);
      syncFiltersFromSearchFilters(nextSearchFilters);
      if (options?.nextCategories) {
        updateCategories(options.nextCategories);
      }

      try {
        const page = await searchShopperProducts(request, { surface: "brand_grids" });
        if (activeRequestIdRef.current !== requestId) {
          return;
        }

        setSearch((current) => ({ ...current, effectiveQuery: page.effectiveQuery }));
        setSearchResults(page.items.map(productFromListItem));
        setNextCursor(page.nextCursor ?? null);
      } catch (error) {
        if (activeRequestIdRef.current !== requestId) {
          return;
        }

        console.error("[BrandResultsExperience] Failed to execute search:", error);
        setSearchResults([]);
        setNextCursor(null);
      } finally {
        if (activeRequestIdRef.current === requestId) {
          setIsSearching(false);
        }
      }
    },
    [syncFiltersFromSearchFilters, updateCategories]
  );

  const handleColorChange = React.useCallback(
    (selectedColors: string[]) => {
      const nextFacets = searchParamFacets.filter((facet) => facet.name !== "color_family");
      const updatedFacets =
        selectedColors.length > 0
          ? upsertFacet(nextFacets, "color_family", selectedColors)
          : nextFacets;

      void executeTextSearch(
        {
          ...searchFilters,
          facets: updatedFacets,
        },
        searchBreadcrumbPath
      );
    },
    [executeTextSearch, searchBreadcrumbPath, searchParamFacets, searchFilters]
  );

  const handlePriceChange = React.useCallback(
    (priceRange: [number, number]) => {
      const rawMin = priceRange[0] > PRICE_RANGE_MIN_DEFAULT ? priceRange[0] : null;
      const rawMax = priceRange[1] < PRICE_RANGE_MAX_DEFAULT ? priceRange[1] : null;

      void executeTextSearch(
        applyPriceFilters(
          {
            ...searchFilters,
            facets: searchParamFacets,
          },
          rawMin,
          rawMax
        ),
        searchBreadcrumbPath
      );
    },
    [executeTextSearch, searchBreadcrumbPath, searchParamFacets, searchFilters]
  );

  const handleAdvancedAttributeChange = React.useCallback(
    (attributeHandle: string, value: string) => {
      const nextFacets = [...searchParamFacets];
      const existingFacet = nextFacets.find((facet) => facet.name === attributeHandle);
      const currentValues = existingFacet?.values ?? [];
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter((currentValue) => currentValue !== value)
        : [...currentValues, value];

      const filteredFacets = nextFacets.filter((facet) => facet.name !== attributeHandle);
      const updatedFacets =
        updatedValues.length > 0
          ? upsertFacet(filteredFacets, attributeHandle, updatedValues)
          : filteredFacets;

      void executeTextSearch(
        {
          ...searchFilters,
          facets: updatedFacets,
        },
        searchBreadcrumbPath
      );
    },
    [executeTextSearch, searchBreadcrumbPath, searchParamFacets, searchFilters]
  );

  const handleRemoveFilter = React.useCallback(
    (attribute: string, value: string) => {
      if (attribute === "color_family") {
        const selectedColors =
          searchParamFacets.find((facet) => facet.name === "color_family")?.values ?? [];
        handleColorChange(selectedColors.filter((color) => color !== value));
        return;
      }

      const nextFacets = searchParamFacets
        .map((facet) => {
          if (facet.name !== attribute) {
            return facet;
          }

          // Narrowing a facet is the shopper's edit: it is theirs now.
          return withCallerValues(
            facet,
            facet.values.filter((facetValue) => facetValue !== value)
          );
        })
        .filter((facet) => facet.values.length > 0);

      void executeTextSearch(
        {
          ...searchFilters,
          facets: nextFacets,
        },
        searchBreadcrumbPath
      );
    },
    [executeTextSearch, handleColorChange, searchBreadcrumbPath, searchParamFacets, searchFilters]
  );

  const handleCategoryNavigate = React.useCallback(
    (path: string) => {
      const normalizedSegments = path
        .split(" > ")
        .map((segment) => segment.trim())
        .filter(Boolean);

      const taxonomySegments =
        normalizedSegments[0]?.toLowerCase() === brandName.toLowerCase()
          ? normalizedSegments.slice(1)
          : normalizedSegments;

      const retainedFacets = searchParamFacets.filter((facet) =>
        shouldRetainFacetForCategoryNavigation(facet.name)
      );

      if (taxonomySegments.length === 0) {
        void executeTextSearch(
          {
            ...searchFilters,
            facets: upsertFacet(retainedFacets, "brand_name", [brandName]),
          },
          [brandName],
          { nextCategories: [] }
        );
        return;
      }

      if (!browseMenuIndex) {
        return;
      }

      const taxonomyPath = taxonomySegments.join(" > ");
      const menuNode = getNodeByDisplayPath(browseMenuIndex, taxonomyPath);
      if (!menuNode) {
        return;
      }

      let nextFacets = (menuNode.search_query.facets || []).map((facet) => copyFacet(facet));

      retainedFacets.forEach((facet) => {
        nextFacets = upsertFacet(nextFacets, facet.name, facet.values, {
          source: facet.source,
          stage: facet.stage,
        });
      });
      nextFacets = upsertFacet(nextFacets, "brand_name", [brandName]);

      void executeTextSearch(
        {
          ...searchFilters,
          facets: nextFacets,
        },
        [brandName, ...menuNode.display_path],
        { nextCategories: [] }
      );
    },
    [brandName, browseMenuIndex, executeTextSearch, searchParamFacets, searchFilters]
  );

  const handleLeafCategoryToggle = React.useCallback(
    (leafPath: string) => {
      const segments = leafPath.split(" > ").map((segment) => segment.trim());
      if (segments.length < 2) {
        return;
      }

      const categorySegments = segments.slice(1);
      const leafDepth = categorySegments.length;
      const isSelected = filters.categories.includes(leafPath);
      const nextCategories = isSelected
        ? filters.categories.filter((pathItem) => pathItem !== leafPath)
        : [...filters.categories, leafPath];

      const facetName = `category_path.depth_${leafDepth}`;
      const valuesAtDepth = nextCategories
        .filter(
          (pathItem) =>
            pathItem.split(" > ").map((segment) => segment.trim()).length - 1 === leafDepth
        )
        .map((pathItem) => {
          const pathSegments = pathItem.split(" > ").map((segment) => segment.trim());
          return pathSegments[pathSegments.length - 1].toLowerCase();
        });

      const filteredFacets = searchParamFacets.filter((facet) => facet.name !== facetName);
      const nextFacets =
        valuesAtDepth.length > 0
          ? upsertFacet(filteredFacets, facetName, valuesAtDepth)
          : filteredFacets;

      const nextBreadcrumbPath = !isSelected
        ? [brandName, ...segments]
        : nextCategories.length > 0
          ? [brandName, ...nextCategories[0].split(" > ").map((segment) => segment.trim())]
          : [brandName];

      void executeTextSearch(
        {
          ...searchFilters,
          facets: nextFacets,
        },
        nextBreadcrumbPath,
        { nextCategories }
      );
    },
    [brandName, executeTextSearch, filters.categories, searchParamFacets, searchFilters]
  );

  const loadMoreResults = React.useCallback(async () => {
    if (isLoadingMore || isSearching) {
      return;
    }

    // Load-more requests intentionally reuse the current request id instead of
    // incrementing it. A newer search/filter action will bump the ref, which
    // makes this in-flight pagination request bail out as stale.
    const requestId = activeRequestIdRef.current;

    if (brandBrowseSeed?.hasMore) {
      const currentBrowseSeed = brandBrowseSeed;
      setIsLoadingMore(true);
      setLoadMoreError(null);
      try {
        const response = await fetch(
          `/api/brands/${encodeURIComponent(currentBrowseSeed.brandIdentifier)}/browse?limit=${currentBrowseSeed.limit}&offset=${currentBrowseSeed.nextOffset}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch brand browse results: ${response.status}`);
        }

        const data = (await response.json()) as BrandBrowseResponse;
        if (activeRequestIdRef.current !== requestId) {
          return;
        }

        const newProducts = Array.isArray(data.products) ? data.products : [];
        setSearchResults((previousResults) => appendUniqueProducts(previousResults, newProducts));
        setBrandBrowseSeed({
          brandIdentifier: currentBrowseSeed.brandIdentifier,
          limit: typeof data.limit === "number" ? data.limit : currentBrowseSeed.limit,
          nextOffset:
            (typeof data.offset === "number" ? data.offset : currentBrowseSeed.nextOffset) +
            newProducts.length,
          hasMore: Boolean(data.has_more),
        });
      } catch (error) {
        console.error("[BrandResultsExperience] Failed to load more brand browse results:", error);
        if (activeRequestIdRef.current === requestId) {
          setLoadMoreError("Failed to load more products. Try again.");
        }
      } finally {
        if (activeRequestIdRef.current === requestId) {
          setIsLoadingMore(false);
        }
      }
      return;
    }

    const shopperState = searchRef.current;
    if (typeof nextCursor !== "string" || nextCursor.length === 0) {
      return;
    }

    setIsLoadingMore(true);
    setLoadMoreError(null);
    try {
      let page;
      try {
        page = await searchShopperProducts(
          { ...shopperState.request, cursor: nextCursor },
          { surface: "brand_grids" }
        );
      } catch (error) {
        if (!(error instanceof InvalidSearchCursorError)) throw error;
        // The route refused the cursor: restart, keep what is shown.
        page = await searchShopperProducts(shopperState.request, { surface: "brand_grids" });
      }

      if (activeRequestIdRef.current !== requestId) {
        return;
      }

      const newProducts = page.items.map(productFromListItem);
      setSearchResults((previousResults) => appendUnseenProducts(previousResults, newProducts));
      setNextCursor(page.nextCursor ?? null);
    } catch (error) {
      console.error("[BrandResultsExperience] Failed to load more filtered results:", error);
      if (activeRequestIdRef.current === requestId) {
        setLoadMoreError("Failed to load more products. Try again.");
      }
    } finally {
      if (activeRequestIdRef.current === requestId) {
        setIsLoadingMore(false);
      }
    }
  }, [brandBrowseSeed, isLoadingMore, isSearching, nextCursor]);

  const toggleSortMenu = React.useCallback(() => {
    setIsSortMenuOpen((previous) => !previous);
  }, []);

  const handleSortSelection = React.useCallback((nextSort: SortOrder) => {
    setSortOrder(nextSort);
    setIsSortMenuOpen(false);
  }, []);

  React.useEffect(() => {
    if (searchResults.length === 0) {
      setIsSortMenuOpen(false);
    }
  }, [searchResults]);

  React.useEffect(() => {
    if (!isSortMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.closest("[data-sort-control]")) {
        return;
      }

      setIsSortMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSortMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSortMenuOpen]);

  const sortedResults = React.useMemo(() => {
    if (sortOrder === "priceLowToHigh") {
      return sortProductsByPrice(searchResults, "asc");
    }

    if (sortOrder === "priceHighToLow") {
      return sortProductsByPrice(searchResults, "desc");
    }

    return searchResults;
  }, [searchResults, sortOrder]);

  const activeFilters = React.useMemo((): FilterPill[] => {
    return searchParamFacets.flatMap((facet) => {
      if (!shouldRenderFacetAsAttributePill(facet.name)) {
        return [];
      }

      return facet.values.map((value) => ({
        id: `${facet.name}-${value}`,
        label: formatFilterLabel(facet.name, String(value)),
        variant: "active" as const,
        justification: formatFacetDisplayName(facet.name),
        onRemove: () => handleRemoveFilter(facet.name, String(value)),
      }));
    });
  }, [handleRemoveFilter, searchParamFacets]);

  const {
    isSticky,
    filtersExpanded,
    setFiltersExpanded,
    filtersHeight,
    filtersSectionTopRef,
    filtersContentRef,
  } = useStickyFiltersBar({
    isSearching,
    isLoadingAssistant: false,
  });

  const categoryTree = React.useMemo(() => {
    if (!browseMenuIndex) {
      return [];
    }

    const parentPath =
      filters.categoryNavigationStack.length > 0
        ? filters.categoryNavigationStack[filters.categoryNavigationStack.length - 1].path
        : null;
    const parentNode = parentPath ? getNodeByDisplayPath(browseMenuIndex, parentPath) : null;
    const nodes = parentNode ? parentNode.children_nodes || [] : browseMenuIndex.roots;

    return nodes.map((node) => ({
      name: node.display_name,
      path: node.display_path.join(" > "),
      children: node.children_nodes && node.children_nodes.length > 0 ? [] : undefined,
    }));
  }, [browseMenuIndex, filters.categoryNavigationStack]);

  const selectedAdvancedAttributesMap = React.useMemo(
    () => new Map(Object.entries(filters.advanced).map(([key, values]) => [key, new Set(values)])),
    [filters.advanced]
  );

  const taxonomyBreadcrumbPath = React.useMemo(() => {
    if (searchBreadcrumbPath[0]?.toLowerCase() === brandName.toLowerCase()) {
      return searchBreadcrumbPath.slice(1);
    }
    return searchBreadcrumbPath;
  }, [brandName, searchBreadcrumbPath]);

  const currentCategoryPath =
    taxonomyBreadcrumbPath.length > 0 ? taxonomyBreadcrumbPath.join(" > ") : undefined;
  const productCount = sortedResults.length;
  const hasProducts = productCount > 0;
  const hasMoreResults =
    Boolean(brandBrowseSeed?.hasMore) || Boolean(nextCursor && nextCursor.length > 0);
  const isCategoryNavigationEnabled = !isLoadingBrowseMenu && !browseMenuError;

  return {
    searchFilters,
    searchBreadcrumbPath,
    sortOrder,
    isSortMenuOpen,
    isSearching,
    isLoadingMore,
    loadMoreError,
    isMobileFiltersOpen,
    mobileSuggestionsExpanded,
    sortDropdownListId,
    filtersExpanded,
    filtersHeight,
    isSticky,
    filtersSectionTopRef,
    filtersContentRef,
    filters,
    activeFilters,
    selectedAdvancedAttributesMap,
    categoryTree,
    currentCategoryPath,
    productCount,
    hasProducts,
    hasMoreResults,
    sortedResults,
    isCategoryNavigationEnabled,
    isLoadingTaxonomy: isLoadingBrowseMenu,
    appliedFiltersCount: getAppliedFiltersCount(),
    setIsMobileFiltersOpen,
    setMobileSuggestionsExpanded,
    setFiltersExpanded,
    updateCategoryNavigationStack,
    toggleSortMenu,
    handleSortSelection,
    restoreInitialState,
    handleCategoryNavigate,
    handleLeafCategoryToggle,
    handlePriceChange,
    handleColorChange,
    handleAdvancedAttributeChange,
    loadMoreResults,
  };
}
